import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getSocketService} from "../services/socketService.js";
import {updateSiteRealtime} from "../store/features/sites/sitesSlice.js";

const SocketContext = createContext({});

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const [socketService] = useState(() => getSocketService());
    const [isConnected, setIsConnected] = useState(false);
    const [realtimeStats, setRealtimeStats] = useState({});
    const [socketId, setSocketId] = useState(null);
    const [activeSites, setActiveSites] = useState(new Set());
    const [recentVisits, setRecentVisits] = useState([]);
    const [connectionError, setConnectionError] = useState(null);
    const [heartbeatStatus, setHeartbeatStatus] = useState('idle');

    // Récupérer les sites depuis Redux
    const sites = useSelector(state => state.sites.items);
    const dispatch = useDispatch();

    // Initialiser la connexion et rejoindre les sites
    useEffect(() => {
       // console.log('🔌 Initialisation SocketContext...');
        socketService.initialize();

        // Écouter les événements
        const handleConnect = (data) => {
           // console.log('✅ Socket connecté dans le contexte:', data);
            setIsConnected(true);
            setSocketId(data.socketId || socketService.getSocketId());
            setConnectionError(null);

            // AUTO-JOIN: Rejoindre automatiquement tous les sites
            autoJoinAllSites();
        };

        const handleDisconnect = (data) => {
           // console.log('🔌 Socket déconnecté:', data?.reason || 'Unknown');
            setIsConnected(false);
            setSocketId(null);
            setActiveSites(new Set());
        };

        const handleConnectError = (error) => {
          ////  console.error('❌ Erreur de connexion Socket:', error);
            setConnectionError(error.message || 'Erreur de connexion');
        };

        const handleVisitUpdate = (data) => {
           // console.log('📊 Mise à jour visite dans contexte:', data);

            // Mettre à jour les stats en temps réel
            setRealtimeStats(prev => ({
                ...prev,
                [data.siteId]: {
                    ...data,
                    lastUpdate: new Date().toISOString()
                }
            }));

            // Mettre à jour Redux pour le site spécifique
            if (data.siteId) {
                dispatch(updateSiteRealtime({
                    siteKey: data.siteId,
                    todayVisites: data.today || 0,
                    totalVisites: data.total || 0,
                    onlineVisitors: data.online || 0,
                    peakVisitors: data.peak || 0,
                    lastUpdated: data.lastUpdated || new Date().toISOString()
                }));
            }
        };

        const handleSiteStats = (data) => {
           // console.log('📈 Stats site dans contexte:', data);

            // Mettre à jour les stats
            setRealtimeStats(prev => ({
                ...prev,
                [data.siteId]: {
                    ...prev[data.siteId],
                    ...data,
                    lastUpdate: new Date().toISOString()
                }
            }));

            // Mettre à jour Redux
            if (data.siteId) {
                dispatch(updateSiteRealtime({
                    siteKey: data.siteId,
                    todayVisites: data.today || 0,
                    totalVisites: data.total || 0,
                    onlineVisitors: data.online || 0,
                    peakVisitors: data.peak || 0,
                    lastUpdated: data.lastUpdated || new Date().toISOString()
                }));
            }
        };

        const handleVisitNew = (data) => {
           // console.log('🎯 Nouvelle visite reçue:', data);

            // Ajouter aux visites récentes
            const newVisit = {
                id: data.visite?.id || Date.now(),
                ip: data.visite?.ip || 'N/A',
                city: data.visite?.city || 'Localisation inconnue',
                country: data.visite?.country || '',
                device: data.visite?.device || 'Desktop',
                visitedAt: data.visite?.visitedAt || new Date().toISOString(),
                siteId: data.site?.id || data.siteId,
                siteName: data.site?.name || data.siteId
            };

            setRecentVisits(prev => {
                const updated = [newVisit, ...prev];
                // Garder seulement les 10 dernières visites
                return updated.slice(0, 10);
            });

            // Mettre à jour les stats
            if (data.site?.id || data.siteId) {
                const siteKey = data.site?.id || data.siteId;
                setRealtimeStats(prev => {
                    const currentStats = prev[siteKey] || {};
                    return {
                        ...prev,
                        [siteKey]: {
                            ...currentStats,
                            total: data.site?.totalVisits || currentStats.total || 0,
                            today: (currentStats.today || 0) + 1,
                            lastVisit: newVisit.visitedAt,
                            lastUpdate: new Date().toISOString()
                        }
                    };
                });
            }
        };

        const handleHeartbeatAck = (data) => {
            setHeartbeatStatus('success');
            setTimeout(() => setHeartbeatStatus('idle'), 2000);
        };

        const handleSiteJoined = (data) => {
            if (data?.siteKey) {
               // console.log('🔗 Site rejoint:', data.siteKey);
                setActiveSites(prev => {
                    const newSet = new Set(prev);
                    newSet.add(data.siteKey);
                    return newSet;
                });
            }
        };

        const handleSiteLeft = (siteKey) => {
           // console.log('🔓 Site quitté:', siteKey);
            setActiveSites(prev => {
                const newSet = new Set(prev);
                newSet.delete(siteKey);
                return newSet;
            });
        };

        // Fonction pour rejoindre automatiquement tous les sites
        const autoJoinAllSites = async () => {
            if (!sites || sites.length === 0) return;

          //  console.log('🔗 Auto-join des sites:', sites.length, 'sites trouvés');

            // Attendre un peu que la connexion soit stable
            setTimeout(async () => {
                for (const site of sites) {
                    const siteKey = site.siteKey || site.id;
                    if (siteKey) {
                        try {
                            await joinSite(siteKey);
                            console.log(`✅ Auto-joined: ${siteKey}`);
                        } catch (error) {
                            console.warn(`⚠️ Échec auto-join pour ${siteKey}:`, error.message);
                        }
                    }
                }
            }, 1000);
        };

        // S'abonner aux événements
        socketService.on('connect', handleConnect);
        socketService.on('disconnect', handleDisconnect);
        socketService.on('connect_error', handleConnectError);
        socketService.on('visit:update', handleVisitUpdate);
        socketService.on('visit:new', handleVisitNew);
        socketService.on('site:stats', handleSiteStats);
        socketService.on('heartbeat-ack', handleHeartbeatAck);

        // Écouter les événements personnalisés pour suivre les joins/quits
        const originalJoinSite = socketService.joinSite;
        socketService.joinSite = async function(...args) {
            const result = await originalJoinSite.apply(this, args);
            if (result?.siteKey) {
                handleSiteJoined(result);
            }
            return result;
        };

        const originalLeaveSite = socketService.leaveSite;
        socketService.leaveSite = function(...args) {
            const result = originalLeaveSite.apply(this, args);
            if (args[0]) {
                handleSiteLeft(args[0]);
            }
            return result;
        };

        // Tester la connexion immédiatement
        const testConnection = () => {
            if (socketService.isConnected()) {
                handleConnect({ socketId: socketService.getSocketId() });
            }
        };

        testConnection();

        // Rejoindre automatiquement les sites quand la liste change
        const joinNewSites = () => {
            if (isConnected && sites && sites.length > 0) {
                sites.forEach(site => {
                    const siteKey = site.siteKey || site.id;
                    if (siteKey && !activeSites.has(siteKey)) {
                        joinSite(siteKey).catch(() => {});
                    }
                });
            }
        };

        // Nettoyage
        return () => {
           // console.log('🧹 Nettoyage SocketContext...');
            socketService.off('connect', handleConnect);
            socketService.off('disconnect', handleDisconnect);
            socketService.off('connect_error', handleConnectError);
            socketService.off('visit:update', handleVisitUpdate);
            socketService.off('visit:new', handleVisitNew);
            socketService.off('site:stats', handleSiteStats);
            socketService.off('heartbeat-ack', handleHeartbeatAck);

            // Restaurer les méthodes originales
            socketService.joinSite = originalJoinSite;
            socketService.leaveSite = originalLeaveSite;

            socketService.disconnect();
        };
    }, [socketService]);

    // Fonction pour rejoindre un site
    const joinSite = useCallback(async (siteKey) => {
        try {
            const result = await socketService.joinSite(siteKey);
            if (result?.siteKey) {
               // console.log('✅ Site rejoint via contexte:', siteKey);
                setActiveSites(prev => {
                    const newSet = new Set(prev);
                    newSet.add(siteKey);
                    return newSet;
                });
            }
            return result;
        } catch (error) {
           // console.error('❌ Erreur joinSite via contexte:', error);
            setConnectionError(error.message || 'Erreur lors de la connexion au site');
            throw error;
        }
    }, [socketService]);

    // Fonction pour quitter un site
    const leaveSite = useCallback(async (siteKey) => {
        try {
            await socketService.leaveSite(siteKey);
           // console.log('✅ Site quitté via contexte:', siteKey);
            setActiveSites(prev => {
                const newSet = new Set(prev);
                newSet.delete(siteKey);
                return newSet;
            });

            // Mettre à jour les stats
            setRealtimeStats(prev => {
                const newStats = { ...prev };
                delete newStats[siteKey];
                return newStats;
            });
        } catch (error) {
           // console.error('❌ Erreur leaveSite via contexte:', error);
        }
    }, [socketService]);

    // Fonction pour envoyer un heartbeat
    const sendHeartbeat = useCallback(() => {
        if (socketService.isConnected()) {
            setHeartbeatStatus('sending');
            const success = socketService.sendHeartbeat();
            if (!success) {
                setHeartbeatStatus('error');
                setTimeout(() => setHeartbeatStatus('idle'), 2000);
            }
        }
    }, [socketService]);

    // Fonction pour obtenir les stats en temps réel
    const getRealtimeStats = useCallback(async (siteKey) => {
        try {
            return await socketService.getRealtimeStats(siteKey);
        } catch (error) {
            console.error('❌ Erreur getRealtimeStats:', error);
            return {
                online: 0,
                total: 0,
                today: 0,
                peak: 0,
                lastUpdated: new Date().toISOString()
            };
        }
    }, [socketService]);

    // Fonction pour obtenir l'historique
    const getVisitHistory = useCallback(async (siteKey, days = 30) => {
        try {
            return await socketService.getVisitHistory(siteKey, days);
        } catch (error) {
           // console.error('❌ Erreur getVisitHistory:', error);
            return { success: false, error: error.message };
        }
    }, [socketService]);

    // Fonction pour obtenir les stats consolidées
    const getGlobalStats = useCallback(() => {
        const stats = Object.values(realtimeStats);

        const totalOnline = stats.reduce((sum, stat) => sum + (stat.online || 0), 0);
        const totalVisits = stats.reduce((sum, stat) => sum + (stat.total || 0), 0);
        const totalToday = stats.reduce((sum, stat) => sum + (stat.today || 0), 0);
        const peakToday = stats.reduce((max, stat) => Math.max(max, stat.peak || 0), 0);

        return {
            totalOnline,
            totalVisits,
            totalToday,
            peakToday,
            monitoredSites: activeSites.size,
            lastUpdate: new Date().toISOString()
        };
    }, [realtimeStats, activeSites]);

    // Fonction pour vider les visites récentes
    const clearRecentVisits = useCallback(() => {
        setRecentVisits([]);
    }, []);

    // Fonction pour rejoindre tous les sites
    const joinAllSites = useCallback(async () => {
        if (!sites || sites.length === 0) {
           // console.warn('⚠️ Aucun site à rejoindre');
            return [];
        }

        console.log('🔗 Tentative de rejoindre tous les sites:', sites.length);
        const results = [];

        for (const site of sites) {
            const siteKey = site.siteKey || site.id;
            if (siteKey) {
                try {
                    const result = await joinSite(siteKey);
                    results.push({ siteKey, success: true, result });
                  //  console.log(`✅ Site ${siteKey} rejoint`);
                } catch (error) {
                    results.push({ siteKey, success: false, error: error.message });
                   // console.warn(`❌ Échec rejoindre ${siteKey}:`, error.message);
                }
                // Petit délai pour éviter les flood
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        return results;
    }, [sites, joinSite]);

    // Valeur du contexte
    const value = {
        // État
        isConnected,
        socketId,
        activeSites: activeSites,
        recentVisits: recentVisits,
        realtimeStats: realtimeStats,
        connectionError,
        heartbeatStatus,

        // Service
        socketService,

        // Méthodes
        joinSite,
        leaveSite,
        joinAllSites, // NOUVEAU: Rejoindre tous les sites
        sendHeartbeat,
        getRealtimeStats,
        getVisitHistory,
        getGlobalStats,
        clearRecentVisits,

        // Utilitaires
        isSocketConnected: socketService.isConnected.bind(socketService),
        getSocketId: socketService.getSocketId.bind(socketService),

        // Statut détaillé
        status: {
            connected: isConnected,
            monitoredSites: activeSites.size,
            totalVisits: getGlobalStats().totalVisits,
            onlineNow: getGlobalStats().totalOnline,
            lastUpdate: getGlobalStats().lastUpdate
        }
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
